import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'

import { SearchAndFilters } from '../searchAndFilters/SearchAndFilters'
import { useCandidates } from '../candidatesContext/useCandidatesContext'
import { formatDate, mapTaskToAssigneePickerStatus } from '../../utils/utils'
import sharedStyles from '../CandidatesContainer.module.scss'
import { useTaskAssignees } from '../../hooks/useTaskAssignees'
import { useMarkTaskWontDo } from '../../hooks/useMarkTaskWontDo'

import styles from './MultipleTasksCandidatesTable.module.scss'
import {
  Alert,
  ConfirmationModal,
  FlagAvatar,
  Flex,
  Loader,
  Modal,
  Spacer,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  AssigneePicker,
  Typography
} from '@percent/lemonade'
import { getCountryNameFromAlpha3Code } from '@percent/partner-dashboard/i18n/countries'
import { Task, TaskInGetCandidates, TaskTemplate } from '@percent/partner-dashboard/services/grants/grantsService.types'

const TASK_STATUS_ORDER: Record<TaskInGetCandidates['status'], number> = {
  todo: 0,
  done: 1,
  wont_do: 2
}

const TASK_TEMPLATE_BG = 'var(--colors-primary-50, #fbf8ff)'
const TASK_TEMPLATE_BORDER = '1px solid var(--colors-gray-90, #e9e8eb)'

type TaskTemplateCellProps = Pick<
  ReturnType<typeof useTaskAssignees>,
  | 'getFilteredAssignees'
  | 'getAssignee'
  | 'getSearchProps'
  | 'getTooltipContent'
  | 'handleAssigneeChange'
  | 'loadingTaskAssignees'
  | 'isUpdatingTaskAssignee'
  | 'canAssignTasks'
> & {
  taskTemplate: TaskTemplate
  tasks: TaskInGetCandidates[]
  candidateId: string
  onWontDo: (taskId: string, taskName: string) => void
}

function TaskTemplateCell({
  taskTemplate,
  tasks,
  candidateId,
  getFilteredAssignees,
  getAssignee,
  getSearchProps,
  getTooltipContent,
  handleAssigneeChange,
  loadingTaskAssignees,
  isUpdatingTaskAssignee,
  canAssignTasks,
  onWontDo
}: Readonly<TaskTemplateCellProps>) {
  const { t } = useTranslation()
  const tasksForTemplate =
    tasks
      ?.filter(task => task.templateId === taskTemplate.id)
      .sort((a, b) => TASK_STATUS_ORDER[a.status] - TASK_STATUS_ORDER[b.status]) ?? []

  const filteredAssignees = getFilteredAssignees(taskTemplate.id)
  const cellClassName = styles.taskTemplateDivider

  return (
    <TableCell onClick={e => e.stopPropagation()} className={cellClassName}>
      <Flex gap="4px">
        {tasksForTemplate.map((task, index) => {
          const { status, label } = mapTaskToAssigneePickerStatus(
            { status: task.status, dueByDate: task.deadline } as Task,
            t
          )
          const assignee = getAssignee(task.id, task.assignee)

          return (
            <Flex
              key={`task-${task.id}`}
              flex={1}
              className={status === 'todo' ? styles.todoAssigneePicker : undefined}
            >
              <AssigneePicker
                status={status}
                statusLabel={label}
                assignee={assignee}
                assigneeList={filteredAssignees}
                onAssigneeChange={assignee_ => handleAssigneeChange(task.id, assignee_)}
                search={getSearchProps(taskTemplate.id)}
                isLoading={loadingTaskAssignees || isUpdatingTaskAssignee}
                triggerTestId={`assignee-picker-${candidateId}-${taskTemplate.id}-${index}`}
                tooltipContent={getTooltipContent(task, assignee)}
                disabled={!canAssignTasks || !task.id}
                bottomButtonText={{
                  label: t('grants.tasks.markAsWontDo'),
                  onClick: () => onWontDo(task.id, taskTemplate.name || '')
                }}
              />
            </Flex>
          )
        })}
      </Flex>
    </TableCell>
  )
}

type MultipleTasksCandidatesTableProps = {
  taskTemplates?: TaskTemplate[]
}

export function MultipleTasksCandidatesTable({ taskTemplates = [] }: Readonly<MultipleTasksCandidatesTableProps>) {
  const { t } = useTranslation()
  const { candidates, navigateToCandidate, queryParams } = useCandidates()
  const {
    loadingTaskAssignees,
    isUpdatingTaskAssignee,
    canAssignTasks,
    handleAssigneeChange,
    getFilteredAssignees,
    getAssignee,
    getSearchProps,
    getTooltipContent
  } = useTaskAssignees()
  const { confirmation, isLoading, openConfirmation, closeConfirmation, confirmMarkWontDo } = useMarkTaskWontDo()

  const urlAssigneeEmails = (queryParams.assigneeEmails ?? []).filter((v): v is string => !!v)
  const includeUnassigned = Boolean(queryParams.includeUnassigned)

  const isSearchActive = !!queryParams.searchTerm && queryParams.searchTerm.trim() !== ''
  const assigneeFilterApplied = urlAssigneeEmails.length > 0 || includeUnassigned
  const filtersApplied = isSearchActive || assigneeFilterApplied

  const columnsHeaders: TableColumn[] = [
    {
      id: 'country',
      content: t('table.header.country'),
      isSortable: false,
      isSticky: true,
      props: { width: '100px' }
    },
    {
      id: 'organization',
      content: t('table.header.organization'),
      isSortable: false,
      isSticky: true,
      props: { width: '300px' }
    },
    ...(taskTemplates.length === 0
      ? [{ id: 'submittedAt', content: t('table.header.submittedAt'), isSortable: true, props: { width: '15%' } }]
      : []),
    ...taskTemplates.map(taskTemplate => ({
      id: `task-${taskTemplate.id}`,
      content: taskTemplate.name,
      isSortable: false,
      props: { width: 'auto', borderLeft: TASK_TEMPLATE_BORDER },
      bgColor: TASK_TEMPLATE_BG
    }))
  ]

  if (candidates.isLoading || loadingTaskAssignees) {
    return <Loader />
  }

  const emptyTableTitle = filtersApplied
    ? t('grants.searchEmptyTable.title')
    : t('grants.candidatesInStateTable.emptyTable.title')
  const emptyTableDescription = filtersApplied ? '' : t('grants.candidatesInStateTable.emptyTable.description')
  const illustration = filtersApplied ? 'no-results' : 'no-grant-candidates'

  return (
    <TableProvider
      columns={columnsHeaders}
      totalResults={candidates.totalResults}
      isLoading={candidates.isLoading}
      illustration={illustration}
      emptyTableTitle={emptyTableTitle}
      emptyTableDescription={emptyTableDescription}
      className={sharedStyles.tableWrapper}
    >
      {(candidates.totalResults > 0 || filtersApplied) && (
        <Flex direction="column" data-testid="search-and-filters">
          <SearchAndFilters enabledFilters={{ organisationSearch: true, currentState: false, assignee: true }} />
        </Flex>
      )}
      <Table>
        <TableBody>
          {candidates.data?.map(({ id, organisation, createdAt, tasks }) => {
            const handleWontDoClick = (taskId: string, taskName: string) =>
              openConfirmation({ candidateId: id, taskId, taskName, candidateName: organisation.name })

            return (
              <TableRow
                key={`candidate-${id}`}
                data-testid={`candidate-${id}`}
                onClick={() => navigateToCandidate(id)}
                className={sharedStyles.tableRow}
              >
                <TableCell>
                  <Flex align="center">
                    <Tooltip
                      content={getCountryNameFromAlpha3Code(organisation.countryCode as Alpha3Code)}
                      placement="right"
                    >
                      <div className={sharedStyles.tooltipContent}>
                        <FlagAvatar code3={organisation.countryCode as Alpha3Code} width="28px" />
                      </div>
                    </Tooltip>
                  </Flex>
                </TableCell>
                <TableCell>{organisation.name}</TableCell>

                {taskTemplates.length === 0 && <TableCell>{formatDate(createdAt)}</TableCell>}

                {taskTemplates.map(taskTemplate => (
                  <TaskTemplateCell
                    key={`task-${taskTemplate.id}`}
                    taskTemplate={taskTemplate}
                    tasks={tasks}
                    candidateId={id}
                    getFilteredAssignees={getFilteredAssignees}
                    getAssignee={getAssignee}
                    getSearchProps={getSearchProps}
                    getTooltipContent={getTooltipContent}
                    handleAssigneeChange={handleAssigneeChange}
                    loadingTaskAssignees={loadingTaskAssignees}
                    isUpdatingTaskAssignee={isUpdatingTaskAssignee}
                    canAssignTasks={canAssignTasks}
                    onWontDo={handleWontDoClick}
                  />
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={t('table.results', { count: candidates.totalResults ?? undefined })}
          previousPage={candidates.previousPage}
          nextPage={candidates.nextPage}
        />
      </Flex>
      <Modal open={!!confirmation} onClose={closeConfirmation}>
        <ConfirmationModal
          title={t('grants.tasks.markAsWontDo')}
          primaryButtonText={t('button.confirm')}
          secondaryButtonText={t('button.cancel')}
          loading={isLoading}
          handleClose={closeConfirmation}
          handleSubmit={confirmMarkWontDo}
          primaryBtnTestId="confirm-wont-do"
          secondaryBtnTestId="cancel-wont-do"
        >
          <Alert variant="warning">
            <Typography variant="default-bold">{t('grants.tasks.markAsWontDoWarning')}</Typography>
          </Alert>
          <Spacer axis="vertical" size={4} />
          <Typography variant="default">
            {t('grants.tasks.markAsWontDoConfirmation', {
              taskName: confirmation?.taskName,
              candidateName: confirmation?.candidateName
            })}
          </Typography>
        </ConfirmationModal>
      </Modal>
    </TableProvider>
  )
}
